import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import store from '../../utils/store';
import { useMediaUpload } from '../../hooks/useMediaUpload';
import {
  ACTION_TYPES_UPGRADE,
  VERSION_TYPES_UPGRADE,
  EDIT_MODES,
  LOADING_STATES
} from '../../constants/config_upgrade';

// ✅ FIX: Use new zone-based selectors
import {
  selectSharedDataForComponents
} from '../selectors/zoneSelectors';

// ✅ FIX: Import only the actions we actually use from the new slice
import {
  loadVariationForEdit,
  loadVersionForEdit,
  loadDefaultVersion,
  loadVersionForClone,
  saveVariation,
  updateActiveSample,
  updateVersionField,
  setActiveVersionFromSamples,
  deleteVersion,
  deleteVariation as deleteVariationAction,
  updateTextSnippetDb,
  updateFormField,
  setFormDirty,
  setFormSubmitting,
  setEditError,
  clearEditError,
  clearEditForm,
  setRepositoryVersion,
  showErrorHint
} from '../slices/bookWorksheetDataSlice';

// Import real upload functions from the recovered utilities slice
import {
  updateTextSnippetOnly,
  uploadTextSnippet,
  uploadVoiceSnippet,
  updateVoiceSnippetDb
} from '../slices/bwdUtilitiesSlice';

// ===== BASE SELECTORS =====
const selectVariationZone = (state) => state.user.bookWorksheetData?.variationZone;

// ===== MEMOIZED SELECTORS FOR PERFORMANCE =====
const selectEditVersion = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVersion || null;
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[SELECTOR] selectEditVersion:', result);
    }
    return result;
  }
);

const selectEditVariationData = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVariation || null;
    // Remove excessive logging - only log when explicitly debugging
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[SELECTOR] selectEditVariationData :', result);
    }
    return result;
  }
);

const selectActiveVersion = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.activeVersion || null;
    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[SELECTOR] selectActiveVersion:', result);
    }
    return result;
  }
);

const selectEditVersionSamples = createSelector(
  [selectVariationZone],
  (variationZone) => {
    const result = variationZone?.samples || [];

    // Sort by version number (ascending: 1, 2, 3...)
    const sortedResult = [...result].sort((a, b) => {
      const versionA = parseInt(a.version) || 0;
      const versionB = parseInt(b.version) || 0;
      return versionA - versionB;
    });

    if (process.env.NODE_ENV === 'development' && window.DEBUG_SELECTORS) {
      console.log('[SELECTOR] selectEditVersionSamples:', sortedResult?.length, 'samples (sorted by version)');
    }
    return sortedResult;
  }
);


const selectEditMode = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.editMode || 'view'
);

const selectEditLoadingState = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.loading ? 'LOADING' : 'IDLE'
);

const selectEditError = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.error || null
);

const selectHasUnsavedChanges = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.hasUnsavedChanges || false
);

const selectIsSubmitting = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.isSubmitting || false
);

const selectIsLoading = createSelector(
  [selectVariationZone],
  (variationZone) => variationZone?.loading || false
);

// ✅ FIX: Create dummy selectors for fields that don't exist yet
const createDummySelector = (defaultValue) => () => defaultValue;

const selectCurrentAction = createDummySelector(null);
const selectEditErrorType = createDummySelector(null);
const selectIsFormDirty = createDummySelector(false);
const selectShowErrorHint = createDummySelector(false);
const selectErrorHintMessage = createDummySelector('');
const selectRepositoryVersion = createDummySelector(null);
const selectLastAction = createDummySelector(null);
const selectEditLastUpdated = createDummySelector(null);
const selectCanSave = createDummySelector(false);
const selectCanCancel = createDummySelector(false);
const selectFormValues = createDummySelector({});
// const selectSelections = createDummySelector({}); // Removed - unused

/**
 * Enhanced hook to manage edit version operations through Redux
 * Provides a clean interface for all variation/version editing operations
 * Replaces the complex useEffect patterns with centralized Redux actions
 */
const useEditVersionManager = () => {
  const dispatch = useDispatch();

  // ✅ NEW: Get uploadMedia function from useMediaUpload hook
  const { uploadMedia } = useMediaUpload();

  // Get current selections from new zone-based selectors
  const sharedData = useSelector(selectSharedDataForComponents);
  // const characterData = useSelector(selectCharacterZoneData); // Not currently used

  // Extract selections from new data structure
  const litGroupId = sharedData?.selections?.litGroupId;
  const bookId = sharedData?.selections?.bookId;
  const characterId = sharedData?.selections?.characterId;

  // Create dummy objects for compatibility (commented out - unused)
  // const selectedLitGroup = litGroupId ? { uuid: litGroupId } : null;
  // const selectedBook = bookId ? { uuid: bookId } : null;
  // const selectedCharacter = characterData?.selectedCharacter || (characterId ? { uuid: characterId } : null);

  // Get edit state
  const editVersion = useSelector(selectEditVersion);
  const editVariationData = useSelector(selectEditVariationData);
  const activeVersion = useSelector(selectActiveVersion);
  const samples = useSelector(selectEditVersionSamples);
  const editMode = useSelector(selectEditMode);
  const currentAction = useSelector(selectCurrentAction);
  const loadingState = useSelector(selectEditLoadingState);
  const error = useSelector(selectEditError);
  const errorType = useSelector(selectEditErrorType);
  const isDirty = useSelector(selectIsFormDirty);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const isSubmitting = useSelector(selectIsSubmitting);
  const showErrorHintState = useSelector(selectShowErrorHint);
  const errorHintMessage = useSelector(selectErrorHintMessage);
  const repositoryVersion = useSelector(selectRepositoryVersion);
  const lastAction = useSelector(selectLastAction);
  const lastUpdated = useSelector(selectEditLastUpdated);
  const canSave = useSelector(selectCanSave);
  const canCancel = useSelector(selectCanCancel);
  const isLoading = useSelector(selectIsLoading);
  const formValues = useSelector(selectFormValues);


  // ===== UTILITY HELPERS =====

  /**
   * Format validation errors for user display
   * @param {Array} errors - Array of error messages
   * @returns {string} Formatted error message
   */
  const formatValidationErrors = useCallback((errors) => {
    if (errors.length === 0) return '';
    if (errors.length === 1) return errors[0];

    return `Please fix the following issues before saving:\n\n${errors.map(error => `• ${error}`).join('\n')}`;
  }, []);

  /**
   * Comprehensive validation for variation submission with error handling
   * @param {Object} params - Validation parameters
   * @param {Object} params.variationZone - Current variation zone state
   * @param {Object} params.selections - Current selections (litGroup, book, character)
   * @param {string} params.characterId - Character ID
   * @param {Object} params.localFormState - Local form state
   * @param {string} params.editMode - Current edit mode
   * @param {Function} params.showHint - Function to show user hints
   * @param {boolean} params.show_logs - Flag to control console logging (default: true)
   * @returns {Object} { hasErrors: boolean, errorMessage: string, validationErrors: Array }
   */
  const validateVariationSubmission = useCallback((params) => {
    const {
      variationZone,
      selections,
      characterId,
      localFormState,
      editMode,
      showHint,
      show_logs = true
    } = params;

    const validationErrors = [];

    // 1. System-level validations
    if (!variationZone) {
      if (show_logs) console.error('[validateVariationSubmission] No variation zone found in state');
      validationErrors.push('System error: No variation data found');

      // Early return for critical system error with immediate user feedback
      const errorMessage = 'System error: No variation data found';
      showHint(errorMessage);
      return {
        hasErrors: true,
        errorMessage,
        validationErrors,
        debugInfo: { criticalSystemError: true }
      };
    }

    // 2. Core data validations
    if (!variationZone.activeVariation?.name?.trim()) {
      if (show_logs) console.log('[validateVariationSubmission] Missing variation name');
      validationErrors.push('Variation name is required');
    }

    if (!variationZone.activeVersion?.textSnippet?.trim()) {
      if (show_logs) console.log('[validateVariationSubmission] Missing text content');
      validationErrors.push('Text content is required');
    }

    if (!characterId) {
      if (show_logs) console.log('[validateVariationSubmission] Missing character ID');
      validationErrors.push('Character ID is required');
    }

    // 3. S3-specific validations (only when needed)
    if (localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT) {
      if (!selections?.litGroupId) {
        validationErrors.push('Literary Group selection is required for file updates');
      }
      if (!selections?.bookId) {
        validationErrors.push('Book selection is required for file updates');
      }
      if (!selections?.characterId) {
        validationErrors.push('Character selection is required for file updates');
      }

      // Additional S3-specific checks
      if (!variationZone.activeVariation?.uuid) {
        validationErrors.push('Variation ID is missing for file update');
      }

      if (!variationZone.activeVersion?.uuid) {
        validationErrors.push('Version ID is missing for file update');
      }
    }

    // 4. Content quality validations
    if (variationZone?.activeVersion?.textSnippet) {
      const contentLength = variationZone.activeVersion.textSnippet.length;

      if (contentLength > 10000) {
        validationErrors.push(`Text content is too long (${contentLength} characters). Maximum allowed is 10,000 characters.`);
      }

      if (contentLength < 10) {
        validationErrors.push('Text content is too short. Please provide at least 10 characters.');
      }
    }

    // 5. File naming validations
    if (variationZone?.activeVariation?.name &&
      !/^[a-zA-Z0-9\s\-_]+$/.test(variationZone.activeVariation.name)) {
      validationErrors.push('Variation name contains invalid characters for file storage');
    }

    // 6. Handle validation results with logging and user feedback
    if (validationErrors.length > 0) {
      if (show_logs) {
        console.group('[validateVariationSubmission] Validation Failed');
        console.log('Total errors found:', validationErrors.length);
        console.log('Validation errors:', validationErrors);
        console.log('State snapshot:', {
          hasVariationZone: !!variationZone,
          hasActiveVariation: !!variationZone?.activeVariation,
          hasActiveVersion: !!variationZone?.activeVersion,
          variationName: variationZone?.activeVariation?.name,
          hasTextContent: !!variationZone?.activeVersion?.textSnippet,
          characterId,
          editMode,
          hasLocalChanges: localFormState?.hasLocalChanges,
          needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
        });
        console.groupEnd();
      }

      const errorMessage = formatValidationErrors(validationErrors);
      showHint(errorMessage);

      return {
        hasErrors: true,
        errorMessage,
        validationErrors,
        debugInfo: {
          totalErrors: validationErrors.length,
          needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
        }
      };
    }

    // 7. Success case
    if (show_logs) {
      console.log('[validateVariationSubmission] All validations passed');
    }

    return {
      hasErrors: false,
      errorMessage: '',
      validationErrors: [],
      debugInfo: {
        needsS3Update: localFormState?.hasLocalChanges && editMode === EDIT_MODES.EDIT
      }
    };
  }, [formatValidationErrors]);

  /**
   * Clear the entire form
   */
  const clearForm = useCallback(() => {
    dispatch(clearEditForm());
  }, [dispatch]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    dispatch(clearEditError());
  }, [dispatch]);

  /**
   * Show error hint for 3 seconds
   */
  const showHint = useCallback((message) => {
    dispatch(showErrorHint(message));

    // Auto-clear after 3 seconds
    setTimeout(() => {
      dispatch(clearEditError());
    }, 3000);
  }, [dispatch]);

  // ===== LOAD ACTIONS =====

  /**
   * Load a variation for editing (from VariationList selection)
   */
  const loadVariation = useCallback(async (variationId) => {
    if (!characterId || !litGroupId || !bookId) {
      console.error('[useEditVersionManager] Missing required IDs for loadVariation');
      return;
    }

    console.log(`[useEditVersionManager] Loading variation ${variationId} for edit`);

    try {
      dispatch(loadVariationForEdit({
        variationId,
        characterId,
        litGroupId,
        bookId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to load variation:', error);
    }
  }, [dispatch, characterId, litGroupId, bookId]);

  /**
   * Load a specific version for editing (from VersionsList row/edit/checkbox)
   */
  const loadVersion = useCallback(async (variationId, versionId) => {
    if (!characterId || !litGroupId || !bookId) {
      console.error('[useEditVersionManager] Missing required IDs for loadVersion');
      return;
    }

    console.log(`[useEditVersionManager] Loading version ${versionId} for variation ${variationId}`);

    try {
      dispatch(loadVersionForEdit({
        variationId,
        versionId,
        characterId,
        litGroupId,
        bookId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to load version:', error);
    }
  }, [dispatch, characterId, litGroupId, bookId]);

  /**
   * Load default version for new variation (from New Variation button)
   */
  const loadDefault = useCallback(async () => {
    if (!characterId) {
      console.error('[useEditVersionManager] Missing characterId for loadDefault');
      return;
    }

    console.log(`[useEditVersionManager] Loading default version for character ${characterId}`);

    try {
      dispatch(loadDefaultVersion({ characterId }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to load default version:', error);
    }
  }, [dispatch, characterId]);

  /**
   * Load version for cloning (from Use as Base button)
   */
  const loadClone = useCallback(async (variationId, versionId) => {
    if (!characterId || !litGroupId || !bookId) {
      console.error('[useEditVersionManager] Missing required IDs for loadClone');
      return;
    }

    console.log(`[useEditVersionManager] Loading version ${versionId} for cloning`);

    try {
      dispatch(loadVersionForClone({
        variationId,
        versionId,
        characterId,
        litGroupId,
        bookId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to load version for cloning:', error);
    }
  }, [dispatch, characterId, litGroupId, bookId]);


  const setActiveVersionFromSamplesHook = useCallback((versionId) => {
    console.log('[useEditVersionManager] Setting active version from samples:', versionId);
    dispatch(setActiveVersionFromSamples(versionId));
  }, [dispatch]);

  // ===== SAVE ACTIONS =====

  /**
   * Save the current variation (handles new, update, and clone modes)
   */
  const save = useCallback(async () => {
    if (!editVariationData || !activeVersion || !characterId) {
      console.error('[useEditVersionManager] Missing required data for save');
      return;
    }

    console.log(`[useEditVersionManager] Saving variation in ${editMode} mode. activeVersion ${activeVersion}`);
    // const tmp_activeVersion = store.getState().user.bookWorksheetData.variationZone.activeVersion; // Debug line - removed

    try {
      dispatch(saveVariation({
        variationData: editVariationData,
        versionData: activeVersion,
        editMode,
        characterId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to save variation:', error);
    }
  }, [dispatch, editVariationData, activeVersion, editMode, characterId]);

  // ===== DELETE ACTIONS =====

  /**
   * Delete a version (from VersionsList delete button)
   */
  const deleteVersionById = useCallback(async (versionId, variationId) => {
    if (!characterId) {
      console.error('[useEditVersionManager] Missing characterId for deleteVersion');
      return;
    }

    console.log(`[useEditVersionManager] Deleting version ${versionId}`);

    try {
      dispatch(deleteVersion({
        versionId,
        variationId,
        characterId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to delete version:', error);
    }
  }, [dispatch, characterId]);

  /**
   * Delete entire variation (from form Delete Variation button)
   */
  const deleteVariation = useCallback(async (variationId, options = {}) => {
    if (!variationId) {
      throw new Error('Variation ID is required');
    }

    if (!characterId) {
      throw new Error('Character ID is required');
    }

    console.log(`[useEditVersionManager] Deleting variation ${variationId} with enhanced delete system`);

    try {
      // ✅ FIX: Call deleteVariationAction instead of deleteVersion
      const result = dispatch(deleteVariationAction({
        variationId
      }));

      console.log('✅ Variation deleted successfully with enhanced delete system:', result);
      return result;
    } catch (error) {
      console.error('❌ Enhanced delete failed:', error);
      throw error;
    }
  }, [dispatch, characterId]);


  // ===== ACTIVE SAMPLE ACTIONS =====

  /**
   * Update the active sample for a variation (from VersionsList checkbox)
   */
  const setActiveSample = useCallback(async (variationId, sampleId) => {
    if (!characterId) {
      console.error('[useEditVersionManager] Missing characterId for setActiveSample');
      return;
    }

    console.log(`[useEditVersionManager] Setting active sample ${sampleId} for variation ${variationId}`);

    try {
      dispatch(updateActiveSample({
        variationId,
        sampleId,
        characterId
      }));
    } catch (error) {
      console.error('[useEditVersionManager] Failed to update active sample:', error);
    }
  }, [dispatch, characterId]);

  // ===== FORM FIELD ACTIONS =====

  /**
   * Update a variation field (name, priority, status, etc.)
   */
  const updateVariationField = useCallback((field, value) => {
    console.log(`[useEditVersionManager] Updating variation field ${field}:`, value);
    // TODO: Implement real variation field update action
    // For now, this is a dummy action - variation fields should be updated through form submission
    dispatch(updateFormField({ field, value }));
  }, [dispatch]);

  /**
   * Update a version field (textSnippet, s3Url, etc.)
   */
  const updateActiveVersionField = useCallback(async (field, value) => {
    // Conditional logging for performance
    if (process.env.NODE_ENV === 'development' && window.DEBUG_VARIATION_FORM) {
      console.log(`[useEditVersionManager] Updating version field ${field}:`, value);
    }
    dispatch(updateVersionField({ field, value }));
  }, [dispatch]);

  /**
   * ✅ NEW: Update text snippet only for existing version (solves the textSnippet update issue)
   * This allows updating text content independently of audio
   */
  const updateTextSnippetForVersion = useCallback(async (newTextContent) => {
    if (!activeVersion?.uuid || !editVariationData?.uuid) {
      console.error('[useEditVersionManager] Missing version or variation ID for text update');
      showHint('Cannot update text: missing version information');
      return { success: false, error: 'Missing version information' };
    }

    try {
      console.log('[useEditVersionManager] Updating text snippet for version:', activeVersion.uuid);

      // Get fresh metadata from state
      const currentState = store.getState();
      const selections = currentState.user.bookWorksheetData?.sharedData?.selections;
      // const characterZone = currentState.user.bookWorksheetData?.characterZone; // Removed - unused

      const metadata = {
        litGroupId: selections?.litGroupId || litGroupId,
        bookId: selections?.bookId || bookId,
        characterId: selections?.characterId || characterId,
        variationName: editVariationData.name || 'Unnamed Variation'
      };

      // Use the utilities slice to update text snippet only
      const result = await dispatch(updateTextSnippetOnly({
        versionId: activeVersion.uuid,
        newTextContent,
        variationId: editVariationData.uuid,
        metadata
      }));

      if (result.type.endsWith('/fulfilled')) {
        console.log('[useEditVersionManager] Text snippet updated successfully');

        // Update local Redux state to reflect the change
        dispatch(updateVersionField({ field: 'textSnippet', value: newTextContent }));

        return { success: true, result: result.payload };
      } else {
        console.error('[useEditVersionManager] Text snippet update failed:', result.payload);
        showHint('Failed to update text snippet');
        return { success: false, error: result.payload };
      }
    } catch (error) {
      console.error('[useEditVersionManager] Error updating text snippet:', error);
      showHint('Failed to update text snippet');
      return { success: false, error: error.message };
    }
  }, [dispatch, activeVersion, editVariationData, litGroupId, bookId, characterId, showHint]);

  // ===== UI STATE ACTIONS =====

  /**
   * Set form dirty state
   */
  const setDirty = useCallback((dirty) => {
    dispatch(setFormDirty(dirty));
  }, [dispatch]);

  /**
   * Set form submitting state
   */
  const setSubmitting = useCallback((submitting) => {
    dispatch(setFormSubmitting(submitting));
  }, [dispatch]);

  /**
   * Set error state
   */
  const setError = useCallback((error, errorType) => {
    dispatch(setEditError({ error, errorType }));
  }, [dispatch]);



  /**
   * Set repository version for cancel operations
   */
  const setRepository = useCallback((version) => {
    dispatch(setRepositoryVersion(version));
  }, [dispatch]);

  // ===== CONVENIENCE METHODS =====

  /**
   * Cancel changes and revert to repository version
   */
  const cancel = useCallback(() => {
    if (repositoryVersion) {
      // Reload from repository
      if (repositoryVersion.variation?.uuid) {
        loadVariation(repositoryVersion.variation.uuid);
      } else {
        loadDefault();
      }
    } else {
      clearForm();
    }
  }, [repositoryVersion, loadVariation, loadDefault, clearForm]);

  /**
   * Check if the form can be saved
   */
  const validateForSave = useCallback(() => {
    if (!editVariationData?.name?.trim()) {
      showHint('Variation name is required');
      return false;
    }

    if (!activeVersion?.textSnippet?.trim()) {
      showHint('Text content is required');
      return false;
    }

    return true;
  }, [editVariationData, activeVersion, showHint]);

  /**
   * Save with validation
   */
  const saveWithValidation = useCallback(async () => {
    if (!validateForSave()) {
      return false;
    }
    console.log('ACTIVE VERSION', store.getState().user.bookWorksheetData.variationZone.activeVersion.textSnippet);
    try {
      await save();
      return true;
    } catch (error) {
      showHint('Failed to save variation');
      return false;
    }
  }, [validateForSave, save, showHint]);

  /**
   * ✅ NEW: Enhanced variation submit function with validation, S3 text snippet updates, and voice snippet uploads
   * This eliminates race conditions by getting fresh state at submission time
   */
  const variationSubmit = useCallback(async (localFormState, pendingRecording = null) => {
    try {
      const show_logs = process.env.NODE_ENV === 'development'; // Control logging based on environment

      if (show_logs) console.log('[variationSubmit] Starting submit process with localFormState:', localFormState);

      // 1. Force sync any pending local changes to Redux
      if (localFormState?.hasLocalChanges && localFormState?.content !== undefined) {
        if (show_logs) console.log('[variationSubmit] Syncing local changes to Redux');
        dispatch(updateVersionField({
          field: 'textSnippet',
          value: localFormState.content
        }));
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      // 2. Get fresh state for validation and operations
      const currentState = store.getState();
      const variationZone = currentState.user.bookWorksheetData?.variationZone;
      const selections = currentState.user.bookWorksheetData?.sharedData?.selections;

      // 3. Comprehensive validation with quick return
      const validationResult = validateVariationSubmission({
        variationZone,
        selections,
        characterId,
        localFormState,
        editMode,
        showHint,
        show_logs
      });

      // 4. Quick return if validation failed
      if (validationResult.hasErrors) {
        return {
          success: false,
          error: 'Validation failed',
          validationErrors: validationResult.validationErrors,
          debugInfo: validationResult.debugInfo
        };
      }

      // 5. S3 text update (only if validation passed and needed)
      if (validationResult.debugInfo.needsS3Update) {
        if (show_logs) console.log('[variationSubmit] Text content changed in edit mode, updating S3 file');

        try {
          const s3Metadata = {
            litGroupId: selections.litGroupId,
            bookId: selections.bookId,
            characterId: selections.characterId,
            variationName: variationZone.activeVariation.name
          };

          const textUpdateResult = await dispatch(uploadTextSnippet({
            textContent: variationZone.activeVersion.textSnippet,
            variationId: variationZone.activeVariation.uuid,
            speechSampleId: variationZone.activeVersion.uuid,
            metadata: s3Metadata,
            uploadMedia // Pass the uploadMedia function from the hook
          })).unwrap();

          // Store result but don't use it to avoid ESLint warning
          await dispatch(updateTextSnippetDb({
            bookuuid: selections.bookId,
            sampleuuid: variationZone.activeVersion.uuid,
            content: variationZone.activeVersion.textSnippet,
          })).unwrap();

          if (show_logs) console.log('[variationSubmit] S3 text file updated successfully:', textUpdateResult.textS3Url);
        } catch (error) {
          console.error('[variationSubmit] Failed to update S3 text file:', error);
          showHint('Failed to update text file in storage. Please try again.');
          return { success: false, error: 'S3 text update failed' };
        }
      }

      // 6. ✅ NEW: Voice snippet upload (if pending recording exists)
      if (pendingRecording?.audioBlob) {
        if (show_logs) console.log('[variationSubmit] Processing pending voice recording');

        try {
          const voiceMetadata = {
            litGroupId: selections.litGroupId,
            bookId: selections.bookId,
            characterId: selections.characterId,
            variationName: variationZone.activeVariation.name,
            fileDuration: pendingRecording.metadata?.fileDuration || 0
          };

          const voiceUpdateResult = await dispatch(uploadVoiceSnippet({
            audioBlob: pendingRecording.audioBlob,
            variationId: variationZone.activeVariation.uuid,
            speechSampleId: variationZone.activeVersion.uuid,
            metadata: voiceMetadata,
            uploadMedia // ✅ Pass the uploadMedia function from the hook
          })).unwrap();

          // Update the database with the new voice S3 URL and duration
          await dispatch(updateVoiceSnippetDb({
            bookuuid: selections.bookId,
            sampleuuid: variationZone.activeVersion.uuid,
            s3Url: voiceUpdateResult.voiceS3Url,
            fileDuration: pendingRecording.metadata?.fileDuration || 0
          })).unwrap();

          if (show_logs) console.log('[variationSubmit] Voice recording uploaded successfully:', voiceUpdateResult.voiceS3Url);
        } catch (error) {
          console.error('[variationSubmit] Failed to upload voice recording:', error);
          showHint('Failed to upload voice recording. Please try again.');
          return { success: false, error: 'Voice upload failed' };
        }
      }

      // 7. Main save operation (only if validation passed)
      if (show_logs) console.log('[variationSubmit] Starting main save operation');
      await dispatch(saveVariation({
        variationData: variationZone.activeVariation,
        versionData: variationZone.activeVersion,
        editMode,
        characterId
      })).unwrap();

      if (show_logs) console.log('[variationSubmit] Save completed successfully');

      // 🔍 REDUX STATE AFTER SAVE - Track what the Redux state looks like after save
      const finalState = store.getState();
      const finalVariationZone = finalState.user.bookWorksheetData?.variationZone;
      console.log('[REDUX_STATE_AFTER_SAVE] Redux state after successful save:', {
        editVariationData: finalVariationZone?.activeVariation ? {
          uuid: finalVariationZone.activeVariation.uuid,
          activeSampleUuid: finalVariationZone.activeVariation.activeSampleUuid,
          name: finalVariationZone.activeVariation.name
        } : null,
        activeVersion: finalVariationZone?.activeVersion ? {
          uuid: finalVariationZone.activeVersion.uuid,
          version: finalVariationZone.activeVersion.version,
          isActive: finalVariationZone.activeVersion.isActive
        } : null,
        samples: finalVariationZone?.samples?.map(s => ({
          uuid: s.uuid,
          isActive: s.isActive,
          version: s.version
        })) || []
      });

      return { success: true };

    } catch (error) {
      console.error('[variationSubmit] Failed:', error);
      showHint('Failed to save variation');
      return { success: false, error: error.message };
    }
  }, [dispatch, editMode, characterId, showHint, validateVariationSubmission, uploadMedia]);

  // ===== RETURN INTERFACE =====

  return {
    // State
    editVersion,
    editVariationData,
    activeVersion,
    samples,
    editMode,
    currentAction,
    loadingState,
    error,
    errorType,
    isDirty,
    hasUnsavedChanges,
    isSubmitting,
    showErrorHint: showErrorHintState,  // Boolean state
    errorHintMessage,
    repositoryVersion,
    lastAction,
    lastUpdated,
    canSave,
    canCancel,
    isLoading,
    formValues,

    // Load actions
    loadVariation,
    loadVersion,
    loadDefault,
    loadClone,

    // Save actions
    save,
    saveWithValidation,
    variationSubmit,

    // Delete actions
    deleteVersionById,
    deleteVariation,

    // Active sample actions
    setActiveSample,
    setActiveVersionFromSamples: setActiveVersionFromSamplesHook,

    // Form field actions
    updateVariationField,
    updateActiveVersionField,
    updateTextSnippetForVersion,

    // UI state actions
    setDirty,
    setSubmitting,
    setError,
    clearError,
    showHint,
    clearForm,
    setRepository,

    // Convenience methods
    cancel,
    validateForSave,

    // Constants for components
    ACTION_TYPES: ACTION_TYPES_UPGRADE,
    VERSION_TYPES: VERSION_TYPES_UPGRADE,
    EDIT_MODES,
    LOADING_STATES
  };
};

export default useEditVersionManager;
